import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkSmrtExplorerAccess, checkAiAssistantAccess, checkReportDownloadAccess, readAuthContextFromStorage } from '../../store/user/userService';
import { GlobalAIPanel, useGlobalAIHelper } from '../GlobalAIPanel';
import { invalidateUserProfile } from '../../helpers/userProfile';
import { isClubAdminOnly } from '../../helpers/landingPath';

export const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const aiAssistantEnabled = useSelector(state => state.user.aiAssistantEnabled);
  const userRoles = useSelector(state => state.user.userRoles);
  const userType = useSelector(state => state.user.userType);
  const ai = useGlobalAIHelper();

  const checkUserToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || token === 'undefined') {
      setIsLoggedIn(false);
      localStorage.removeItem('token');
      invalidateUserProfile();
      return navigate('/login');
    }

    // Club admins only have the club screens (plan decision 11) — a main-app
    // URL (bookmark, back button) lands them on their dashboard instead.
    let roles = Array.isArray(userRoles) && userRoles.length > 0 ? userRoles : (userType ? [userType] : []);
    if (roles.length === 0) {
      const hydrated = readAuthContextFromStorage();
      roles = hydrated.roles || [];
    }
    if (isClubAdminOnly(roles)) {
      setIsLoggedIn(false);
      return navigate('/club-admin/dashboard', { replace: true });
    }

    setIsLoggedIn(true);
  }, [navigate, userRoles, userType])

  useEffect(() => {
    checkUserToken();
  }, [isLoggedIn, checkUserToken]);

  // Dispatch permission checks early so guards (e.g. SmrtExplorerGuard)
  // have the state they need before child components mount.
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(checkSmrtExplorerAccess());
      dispatch(checkAiAssistantAccess());
      dispatch(checkReportDownloadAccess());
    }
  }, [isLoggedIn, dispatch]);

  const showGlobalAI = isLoggedIn
    && aiAssistantEnabled === true
    && location.pathname !== '/smrt-explorer';

  return (
    <>
      {isLoggedIn ? children : null}
      {showGlobalAI && (
        <GlobalAIPanel
          isOpen={ai.isOpen}
          onToggle={ai.toggleHelper}
          messages={ai.messages}
          isProcessing={ai.isProcessing}
          currentStatus={ai.currentStatus}
          onSendMessage={ai.sendMessage}
          onResetChat={ai.clearMessages}
          isRecording={ai.isRecording}
          voiceEnabled={ai.voiceEnabled}
          onToggleVoice={() => ai.setVoiceEnabled(!ai.voiceEnabled)}
          voiceLanguage={ai.voiceLanguage}
          onLanguageChange={ai.handleLanguageChange}
        />
      )}
    </>
  );
}
